import { ChevronLeft, ChevronRight } from 'lucide-react';
import { InsAccountModel } from '@/types/home';
import { useState } from 'react';
import { useMemo } from 'react';
import { down, goExoirt } from '@/icons/iconSvg';
import { useTranslation } from 'react-i18next';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ITaskListData, Pages } from '../index';
import { fetchGetTaskListApi } from '@/lib/service/home';
import { LoaderCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), { ssr: true });

export default function DataPanel({
  handleBack,
  currentInsUser,
  taskListData,
  type,
  isUse,
  refreshNumber,
}: {
  handleBack: () => void;
  currentInsUser: InsAccountModel;
  taskListData: ITaskListData;
  type: 'following' | 'followers';
  isUse: any;
  refreshNumber: () => void;
  currentUserPlan?: any;
}) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showPageData, setShowPageData] = useState<Pages>(taskListData.pages);
  const showComments = useMemo(() => {
    return showPageData.data;
  }, [showPageData]);

  const handlePageChange = async (page: number, rowsPerPage?: number) => {
    setCurrentPage(page);
    setRowsPerPage(rowsPerPage || 10);
    const res = await fetchGetTaskListApi(taskListData.task.id.toString(), page, rowsPerPage);
    if (res.code == 0) {
      setShowPageData(res.data.pages);
    }
  };
  const isTaskLoading = useMemo(() => {
    return taskListData.task.status == 0;
  }, [taskListData]);

  const handleDownload = async (typeXlsx: 'xlsx' | 'csv') => {
    if (typeXlsx === 'xlsx') {
      ga_logEvent(EventName.click_export_download_excel);
      window.open(taskListData.task.excel_url, '_blank');
    } else {
      ga_logEvent(EventName.click_export_download_csv);
      window.open(taskListData.task.csv_url, '_blank');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center lg:scroll-m-[100px] scroll-m-[150px]" id="data-panel">
        <button
          className="text-gray-500 text-sm flex items-center gap-1 border border-gray-300 rounded-md mt-4 ml-4 px-2 py-1"
          onClick={() => {
            handleBack();
            refreshNumber();
          }}
        >
          <ChevronLeft size={16} />
          {t('Back')}
        </button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-gray-500 text-sm flex items-center gap-1 border border-gray-300 rounded-md mt-4 mr-4 px-2 py-1"
                onClick={() => {
                  router.push('/dashboard?dashboard_type=toolkit&featureType=follower_export');
                }}
              >
                {t('Go to Dashboard')}
                <ChevronRight size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="w-[270px]">
              {t(
                'Exporting followers may take some time. You can safely close this page and manage your tasks later in the Dashboard.'
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="text-center text-3xl font-bold mt-4">
        {isTaskLoading ? t('Preparing your export') : t('Export completed.')}
      </div>
      {showPageData.total > 0 ? (
        <>
          <div className="text-lg mt-2 flex gap-2 items-center justify-center">
            {isTaskLoading && <LoaderCircle size={24} className="animate-spin text-[#7B67FF]" />}
            <div
              dangerouslySetInnerHTML={{
                __html: t(
                  "Exported: <span style='color: #7B67FF;font-weight: bold;'>xxx1</span> , Maximum allowed:  <span style='color: #7B67FF;font-weight: bold;'>xxx2</span>."
                )
                  .replace('xxx1', taskListData.task.current_export_count.toLocaleString())
                  .replace('xxx2', taskListData.task.export_count.toLocaleString()),
              }}
            ></div>
          </div>
          {!isUse && (
            <div className="flex justify-center items-center gap-4 my-4">
              <span>{t('Want to export more?')}</span>
              <button
                className="text-white font-bold bg-gradient-to-b from-[#E642FF]  to-[#C55EFF] rounded-md px-4 py-[7px] text-sm"
                onClick={() => {
                  ga_logEvent(EventName.click_export_upgrade_now);
                  document.getElementById('pricing-title')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('🚀 Upgrade Now')}
              </button>
            </div>
          )}
          <div className="flex justify-center items-center flex-col lg:flex-row gap-4 my-8 px-2">
            <button
              disabled={taskListData.task.status !== 1}
              className={`text-white font-bold w-full lg:w-auto justify-center  transition-all duration-300 rounded-md px-10 py-[10px] text-sm flex items-center gap-2 ${taskListData.task.status !== 1 ? 'cursor-not-allowed bg-gray-300' : 'bg-[#725CFF] hover:bg-[#725CFF]/80'}`}
              onClick={() => handleDownload('xlsx')}
            >
              <div className="flex items-center gap-2">
                {down}
                <span>{t('Download Excel File')}</span>
              </div>
            </button>
            <button
              disabled={taskListData.task.status !== 1}
              className={`text-white font-bold w-full lg:w-auto justify-center  transition-all duration-300 rounded-md px-10 py-[10px] text-sm flex items-center gap-2 ${taskListData.task.status !== 1 ? 'cursor-not-allowed bg-gray-300' : 'bg-[#725CFF] hover:bg-[#725CFF]/80'}`}
              onClick={() => handleDownload('csv')}
            >
              <div className="flex items-center gap-2">
                {down}
                <span>{t('Download CSV File')}</span>
              </div>
            </button>
          </div>
          <div>
            <div className="border-y border-gray-300 gap-2 flex h-[60px] text-sm items-center text-gray-500">
              <div className="lg:w-[10%] w-[15%] text-center">#</div>
              <div className="lg:w-[10%] w-[35%] text-left">
                <span className="hidden lg:block">{t('Avatar')}</span>
                <span className="lg:hidden">{t('User')}</span>
              </div>
              <div className="w-[15%] text-left hidden lg:block">{t('Username')}</div>
              <div className="w-[15%] text-left hidden lg:block">{t('FullName')}</div>
              <div className="lg:w-[40%] w-[25%] lg:text-left text-center">
                <span className="lg:block hidden">{t('Profile URL')}</span>
                <span className="lg:hidden">{t('Verified')}</span>
              </div>
              <div className="lg:w-[10%] w-[25%] lg:text-left text-center">
                <span className="hidden lg:block">{t('Verified')}</span>
                <span className="lg:hidden">{t('Profile URL')}</span>
              </div>
            </div>
            <ul className="text-black">
              {showComments.map((item, index) => (
                <li key={index} className="flex h-[60px] gap-2 text-sm items-center border-b border-gray-300">
                  <div className="lg:w-[10%] w-[15%] text-center">{(currentPage - 1) * rowsPerPage + index + 1}</div>
                  <div className="lg:w-[10%] w-[35%] text-left flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-[40px] h-[40px] rounded-full flex-shrink-0"
                        onClick={() => {
                          window.open(`https://www.instagram.com/${item?.username}`, '_blank');
                        }}
                      >
                        <LocalImage
                          src={item?.profile_pic_url}
                          alt={item.full_name}
                          width={40}
                          height={40}
                          imageClassName="rounded-full"
                        />
                      </div>
                      <div
                        className="w-[calc(100%-48px)] block lg:hidden lg:pr-2"
                        onClick={() => {
                          window.open(`https://www.instagram.com/${item.username}`, '_blank');
                        }}
                      >
                        <div className="text-sm truncate">{item.full_name || ''}</div>
                        <div className="text-sm text-gray-500 truncate">@{item.username || ''}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="w-[15%] truncate text-left hover:underline hover:text-[#725CFF] cursor-pointer hidden lg:block"
                    onClick={() => {
                      window.open(`https://www.instagram.com/${item.username}`, '_blank');
                    }}
                  >
                    {item.username}
                  </div>
                  <div className="w-[15%] text-left  lg:block hidden">{item.full_name}</div>
                  <div className="lg:w-[40%] w-[25%] lg:text-left text-center">
                    <div
                      className="truncate lg:block hidden hover:underline hover:text-[#725CFF] cursor-pointer "
                      onClick={() => {
                        window.open(`https://www.instagram.com/${item.username}`, '_blank');
                      }}
                    >
                      https://www.instagram.com/{item.username}
                    </div>
                    <div className="lg:hidden font-bold">{item.verified ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="lg:w-[10%] w-[25%] flex justify-center lg:justify-start">
                    <div className="lg:block hidden">{item.verified ? 'Yes' : 'No'}</div>
                    <div
                      className="lg:hidden flex items-center gap-[1px] text-sm cursor-pointer"
                      onClick={() => {
                        window.open(`https://www.instagram.com/${item.username}`, '_blank');
                      }}
                    >
                      <span>{goExoirt}</span>
                      <span className="text-xs font-bold">{t('View Now')}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div>
            {(() => {
              const totalPages = Math.ceil(taskListData.task.current_export_count / rowsPerPage);
              return (
                <div className="flex justify-center items-center gap-4 mb-8 mt-4 lg:flex-row flex-col-reverse">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Rows per page</span>
                    <Select
                      defaultValue={rowsPerPage.toString()}
                      onValueChange={(value) => {
                        handlePageChange(1, Number(value));
                        document.getElementById('data-panel')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <SelectTrigger className="h-8 w-[72px] text-sm">
                        <SelectValue placeholder="10" />
                      </SelectTrigger>
                      <SelectContent className="w-[72px] text-sm">
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className={`border rounded px-3 py-1 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                      onClick={() => handlePageChange(1, rowsPerPage)}
                      disabled={currentPage === 1}
                    >
                      «
                    </button>
                    <button
                      className={`border rounded px-3 py-1 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                      onClick={() => handlePageChange(currentPage - 1, rowsPerPage)}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </button>
                    <button
                      className={`border rounded px-3 py-1 ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                      onClick={() => handlePageChange(currentPage + 1, rowsPerPage)}
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </button>
                    <button
                      className={`border rounded px-3 py-1 ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                      onClick={() => handlePageChange(totalPages, rowsPerPage)}
                      disabled={currentPage === totalPages}
                    >
                      »
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      ) : (
        <div className="text-center text-lg my-6 lg:my-[100px]">
          <div className="text-gray-500 px-4 lg:text-base text-sm">
            {isTaskLoading
              ? t('Your export is being processed. Please wait a moment.')
              : t('No xxx1 to export. Please try another username.').replace(
                  'xxx1',
                  type == 'followers' ? 'followers' : 'following'
                )}
          </div>
        </div>
      )}
    </div>
  );
}
